// src/prototype/components/sep/SEPSectionExecutionStrategy.tsx

import React from 'react';
import { Target, GitBranch, Users, ShieldCheck, Globe, Stethoscope } from 'lucide-react';
import type { StrategicExecutionPlanSections } from '../../services/strategicExecutionPlanService';

interface Finalist {
  implementationEffort: 'Low' | 'Medium' | 'High';
  category: string;
}

export interface SEPSectionExecutionStrategyProps {
  clientName: string;
  finalists: Finalist[];
  executionApproach?: StrategicExecutionPlanSections['execution_approach'];
  /** Shared with Executive Summary — do not re-derive independently. */
  quickWinCount?: number;
}

/** Map pillar `id` to icon per API spec (pillars[].id → icon). */
const PILLAR_ID_ICONS: Record<string, React.ReactNode> = {
  value_first_sequencing: <Globe className="w-4 h-4 text-[#18365E]" />,
  phased_delivery_model: <Stethoscope className="w-4 h-4 text-[#18365E]" />,
  cross_functional_alignment: <Users className="w-4 h-4 text-[#18365E]" />,
  governance_integrated_execution: <ShieldCheck className="w-4 h-4 text-[#18365E]" />,
};

/** Legacy fallback when API sends icon string instead of relying on id. */
const PILLAR_ICON_ALIASES: Record<string, React.ReactNode> = {
  globe: PILLAR_ID_ICONS.value_first_sequencing,
  stethoscope: PILLAR_ID_ICONS.phased_delivery_model,
  people: PILLAR_ID_ICONS.cross_functional_alignment,
  shield: PILLAR_ID_ICONS.governance_integrated_execution,
};

const EffortTierCard: React.FC<{ value: number; label: string; accent?: boolean }> = ({ value, label, accent }) => (
  <div className={`rounded-xl p-4 text-center ${accent ? 'bg-[#FF6E4C]/[0.08] border border-[#FF6E4C]/20' : 'bg-[#18365E]/[0.05] border border-[#18365E]/10'}`}>
    <p className={`text-2xl font-bold leading-none ${accent ? 'text-[#FF6E4C]' : 'text-[#18365E]'}`}>{value}</p>
    <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mt-1.5">{label}</p>
  </div>
);

const PillarCard: React.FC<{ icon: React.ReactNode; title: string; description: string }> = ({ icon, title, description }) => (
  <div className="flex gap-3 p-4 bg-[#F5F7FA] rounded-xl border border-[#e5e7eb]">
    <div className="w-8 h-8 rounded-lg bg-[#18365E]/10 flex items-center justify-center shrink-0 mt-0.5">{icon}</div>
    <div>
      <p className="text-sm font-semibold text-[#18365E] mb-1">{title}</p>
      <p className="text-xs text-[#717182] leading-relaxed">{description}</p>
    </div>
  </div>
);

export const SEPSectionExecutionStrategy: React.FC<SEPSectionExecutionStrategyProps> = ({
  clientName, finalists, executionApproach, quickWinCount,
}) => {
  const effortTier = executionApproach?.effort_tier;
  const quickWins =
    quickWinCount ??
    effortTier?.quick_win ??
    finalists.filter(f => f.implementationEffort === 'Low').length;
  const moderateCount = effortTier?.moderate ?? finalists.filter(f => f.implementationEffort === 'Medium').length;
  const complexCount = effortTier?.complex ?? finalists.filter(f => f.implementationEffort === 'High').length;
  const categories = executionApproach?.category_name?.length
    ? executionApproach.category_name
    : [...new Set(finalists.map(f => f.category))];

  const apiPillars = executionApproach?.pillars;
  const defaultPillars = [
    {
      icon: <Target className="w-4 h-4 text-[#18365E]" />,
      title: 'Value-First Sequencing',
      description: quickWins > 0
        ? `${quickWins} Quick Win initiative${quickWins > 1 ? 's are' : ' is'} prioritized for immediate launch to generate early ROI and executive confidence.`
        : 'Each initiative is sequenced to maximize value delivery at the earliest feasible milestone.',
    },
    {
      icon: <GitBranch className="w-4 h-4 text-[#18365E]" />,
      title: 'Phased Delivery Model',
      description: complexCount > 0
        ? 'Complex initiatives are broken into structured phases, reducing risk and enabling course-correction before full commitment.'
        : 'All initiatives follow a structured phase gate process to ensure delivery confidence at each milestone.',
    },
    {
      icon: <Users className="w-4 h-4 text-[#18365E]" />,
      title: 'Cross-Functional Alignment',
      description: `Initiatives span ${categories.slice(0, 2).join(' and ')}${categories.length > 2 ? ` and ${categories.length - 2} additional domain${categories.length - 2 > 1 ? 's' : ''}` : ''}, requiring coordinated leadership from Clinical, Operations, Finance, and IT.`,
    },
    {
      icon: <ShieldCheck className="w-4 h-4 text-[#18365E]" />,
      title: 'Governance-Integrated Execution',
      description: 'Each initiative includes defined approval gates, executive sponsors, and escalation pathways to ensure alignment remains visible to leadership throughout delivery.',
    },
  ];

  const pillars = apiPillars?.length
    ? apiPillars.map(p => ({
        id: p.id,
        icon: PILLAR_ID_ICONS[p.id] ?? PILLAR_ICON_ALIASES[p.icon] ?? <Target className="w-4 h-4 text-[#18365E]" />,
        title: p.title,
        description: p.description,
      }))
    : defaultPillars.map((p, i) => ({
        id: `default_${i}`,
        ...p,
      }));

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-[3px] self-stretch rounded-full bg-[#FF6E4C] shrink-0 min-h-[40px]" />
        <div>
          <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-widest mb-0.5">Section 2</p>
          <h2 className="text-lg font-bold text-[#18365E] leading-tight">Execution Approach</h2>
          <p className="text-[11px] text-[#717182] mt-0.5">Portfolio delivery approach, sequencing philosophy, and organizational alignment</p>
        </div>
      </div>

      <p className="text-sm text-[#18365E]/80 leading-relaxed mb-6">
        The execution strategy for <strong>{clientName}</strong> employs a phased delivery model designed to maximize early value realization while building organizational capability for more complex initiatives. Quick wins are sequenced first to demonstrate impact and build momentum, followed by structured rollouts of higher-complexity programs.
      </p>

      {effortTier && (
        <div className="mb-6">
          <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">Portfolio Effort Mix</p>
          <div className="grid grid-cols-3 gap-3">
            <EffortTierCard value={quickWins} label="Quick Wins Count" accent={quickWins > 0} />
            <EffortTierCard value={moderateCount} label="Moderate Count" />
            <EffortTierCard value={complexCount} label="High Count" accent={complexCount > 0} />
          </div>
        </div>
      )}

      <div className="mb-6">
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">Execution Approach Pillars</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {pillars.map(p => (
            <PillarCard key={p.id} icon={p.icon} title={p.title} description={p.description} />
          ))}
        </div>
      </div>

      <div className="bg-[#18365E]/[0.04] rounded-xl p-4 border border-[#18365E]/10">
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-2">Strategic Sequencing Rationale</p>
        <p className="text-sm text-[#18365E]/80 leading-relaxed">
          The portfolio is sequenced to build a pattern of delivery success. Early Quick Wins generate tangible ROI within the first two quarters, creating organizational credibility for subsequent higher-investment initiatives. Moderate-complexity programs follow once baseline infrastructure and cross-functional workflows are established. Complex, long-horizon programs are structured as phased pilots to de-risk full commitment.
        </p>
      </div>
    </div>
  );
};

export default SEPSectionExecutionStrategy;